import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { GalleryHorizontalEnd, Heart, User, LayoutDashboard, Users, Inbox, Home, Tag, Bookmark, ThumbsUp, Map } from "lucide-react";
import { useAuth, userMode } from "@/context/AuthContext";
import { useNotifications, CountBadge } from "@/context/NotificationsContext";
import { cn } from "@/lib/utils";

// Swipe uses the stacked-cards icon (a deck of homes to flip through).
const TABS = {
  renter: [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/swipe", label: "Swipe", icon: GalleryHorizontalEnd },
    { to: "/saved", label: "Saved", icon: Bookmark },
    { to: "/likes", label: "Likes", icon: ThumbsUp },
    { to: "/matches", label: "Matches", icon: Heart },
    { to: "/profile", label: "Profile", icon: User },
  ],
  lister: [
    { to: "/lister", label: "Dashboard", icon: LayoutDashboard },
    { to: "/lister/browse", label: "Renters", icon: Users },
    { to: "/lister/applicants", label: "Applicants", icon: Inbox },
    { to: "/pricing", label: "Plans", icon: Tag },
  ],
  guest: [
    { to: "/", label: "Home", icon: Home },
    { to: "/map", label: "Map", icon: Map },
    { to: "/pricing", label: "Pricing", icon: Tag },
    { to: "/auth/login", label: "Log in", icon: User },
  ],
};

// Fixed bottom tab bar: a white bar with rounded top corners and a soft top
// shadow. Icons are hollow (outline) grey by default; the ACTIVE tab's icon
// fills solid orange and its label turns orange. No pill, no glow.
export default function NavBottom() {
  const { user } = useAuth();
  const { matches, messages, likes } = useNotifications();
  const tabs = TABS[user ? userMode(user) : "guest"] || TABS.guest;
  const ref = useRef(null);

  const badgeFor = (to) => {
    if (to === "/matches") return matches + messages;
    if (to === "/lister/applicants") return likes;
    return 0;
  };

  // Pin the bar to the VISUAL viewport bottom so the mobile browser toolbar
  // collapsing never leaves a white gap (no-op on desktop).
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const el = ref.current;
    if (!vv || !el) return undefined;
    let raf = 0;
    const place = () => {
      raf = 0;
      const gap = window.innerHeight - vv.height - vv.offsetTop;
      el.style.transform = gap > 0.5 ? `translateY(${-gap}px)` : "";
    };
    const onChange = () => { if (!raf) raf = requestAnimationFrame(place); };
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    window.addEventListener("scroll", onChange, { passive: true });
    onChange();
    return () => {
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
      window.removeEventListener("scroll", onChange);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <nav
      ref={ref}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        margin: 0,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      className="z-40 rounded-t-[26px] bg-white shadow-[0_-6px_20px_rgba(30,42,56,0.08)] md:hidden"
    >
      <div aria-hidden="true" className="absolute inset-x-0 top-full h-[60px] bg-white" />
      <div className="mx-auto flex max-w-md items-stretch justify-around px-2">
        {tabs.map((tab) => (
          <NavLink
            key={tab.label}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition",
                isActive ? "text-brand-primary" : "text-night-sub hover:text-night-text"
              )
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <tab.icon size={22} strokeWidth={isActive ? 2.4 : 2} />
                  <CountBadge
                    count={badgeFor(tab.to)}
                    className="absolute -right-3 -top-1.5 ring-2 ring-white"
                  />
                </span>
                <span className={isActive ? "font-bold" : ""}>{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
